import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { User, MapPin, Wrench, Phone, LogOut, CheckCircle, Clock, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SKILLS = ["Plumber", "Electrician", "Carpenter", "House Maid", "Cook", "Painter", "Tailor", "AC Repair", "Driver", "Gardener"];

const WorkerDashboard = () => {
  const navigate = useNavigate();
  const [worker, setWorker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", skill: "", area: "" });

  const token = localStorage.getItem("junction_token");

  useEffect(() => {
    if (!token) {
      navigate("/worker-login");
      return;
    }
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/worker/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWorker(res.data);
      setEditForm({ name: res.data.name, skill: res.data.skill, area: res.data.area });
    } catch {
      localStorage.removeItem("junction_token");
      localStorage.removeItem("junction_worker");
      navigate("/worker-login");
    }
    setLoading(false);
  };

  const toggleAvailability = async () => {
    setToggling(true);
    try {
      const res = await axios.patch(`${API}/worker/availability`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWorker(prev => ({ ...prev, available: res.data.available }));
      toast.success(res.data.available ? "You are now Available!" : "Status set to Busy");
    } catch {
      toast.error("Failed to update status");
    }
    setToggling(false);
  };

  const updateProfile = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.patch(`${API}/worker/profile`, editForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWorker(res.data);
      setEditing(false);
      toast.success("Profile updated!");
    } catch {
      toast.error("Failed to update profile");
    }
  };

  const logout = () => {
    localStorage.removeItem("junction_token");
    localStorage.removeItem("junction_worker");
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="animate-pulse text-indigo font-heading text-xl">Loading...</div>
      </div>
    );
  }

  if (!worker) return null;

  return (
    <div className="min-h-screen bg-cream" data-testid="worker-dashboard-page">
      {/* Header */}
      <div className="bg-indigo">
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a href="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-marigold flex items-center justify-center">
                <span className="text-indigo font-heading font-bold text-sm">J</span>
              </div>
            </a>
            <span className="text-white/60 text-sm">Worker Dashboard</span>
          </div>
          <Button variant="ghost" size="sm" onClick={logout} className="text-white/60 hover:text-white hover:bg-white/10 rounded-full" data-testid="worker-logout-button">
            <LogOut size={16} className="mr-1.5" />Logout
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 -mt-6">
        {/* Profile Card */}
        <div className="bg-white rounded-3xl p-8 shadow-sm border border-gray-50 mb-6" data-testid="worker-profile-card">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-marigold/10 flex items-center justify-center">
                <span className="font-heading font-bold text-marigold text-2xl">{worker.name.charAt(0)}</span>
              </div>
              <div>
                <h1 className="font-heading text-2xl font-semibold text-indigo" data-testid="worker-name">{worker.name}</h1>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-sm text-gray-500 flex items-center gap-1"><Wrench size={14} />{worker.skill}</span>
                  <span className="text-sm text-gray-500 flex items-center gap-1"><MapPin size={14} />{worker.area}</span>
                </div>
              </div>
            </div>
            {worker.verified ? (
              <div className="flex items-center gap-1.5 bg-green-50 text-green-700 px-3 py-1.5 rounded-full text-xs font-medium">
                <CheckCircle size={14} />Verified
              </div>
            ) : (
              <div className="flex items-center gap-1.5 bg-amber-50 text-amber-700 px-3 py-1.5 rounded-full text-xs font-medium">
                <Clock size={14} />Pending Verification
              </div>
            )}
          </div>

          <div className="flex items-center gap-1.5 text-sm text-gray-500 mb-6">
            <Phone size={14} /><span>{worker.contact}</span>
          </div>

          {/* Availability Toggle */}
          <div className="bg-cream-alt rounded-2xl p-6" data-testid="availability-section">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-heading font-medium text-indigo text-lg mb-1">Your Availability</h3>
                <p className="text-sm text-gray-500">Toggle to let residents know if you're available for work</p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-sm font-medium ${worker.available ? 'text-green-600' : 'text-amber-600'}`}>
                  {worker.available ? 'Available' : 'Busy'}
                </span>
                <Switch
                  checked={worker.available}
                  onCheckedChange={toggleAvailability}
                  disabled={toggling}
                  className="data-[state=checked]:bg-green-600"
                  data-testid="availability-toggle"
                />
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2">
              <span className={`status-dot ${worker.available ? 'available' : 'busy'}`}></span>
              <span className="text-xs text-gray-400">
                {worker.available ? "Residents can see you and contact you" : "You're hidden from search results"}
              </span>
            </div>
          </div>
        </div>

        {/* Edit Profile */}
        <div className="bg-white rounded-3xl p-8 shadow-sm border border-gray-50" data-testid="edit-profile-section">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-xl font-medium text-indigo">Edit Profile</h2>
            {!editing && (
              <Button variant="outline" size="sm" className="rounded-full border-gray-200 text-indigo" onClick={() => setEditing(true)} data-testid="edit-profile-button">
                Edit
              </Button>
            )}
          </div>
          {editing ? (
            <form onSubmit={updateProfile} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Name</label>
                <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="rounded-xl bg-cream-alt border-none" data-testid="edit-name-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Skill</label>
                <Select value={editForm.skill} onValueChange={(v) => setEditForm({ ...editForm, skill: v })}>
                  <SelectTrigger className="rounded-xl bg-cream-alt border-none" data-testid="edit-skill-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{SKILLS.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Area</label>
                <Input value={editForm.area} onChange={(e) => setEditForm({ ...editForm, area: e.target.value })} className="rounded-xl bg-cream-alt border-none" data-testid="edit-area-input" />
              </div>
              <div className="flex gap-3">
                <Button type="submit" className="bg-indigo text-white hover:bg-indigo/90 rounded-full px-8 font-medium" data-testid="save-profile-button">Save Changes</Button>
                <Button type="button" variant="outline" className="rounded-full" onClick={() => setEditing(false)}>Cancel</Button>
              </div>
            </form>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-cream-alt rounded-xl p-4">
                <p className="text-xs text-gray-400 mb-1">Name</p>
                <p className="font-medium text-indigo">{worker.name}</p>
              </div>
              <div className="bg-cream-alt rounded-xl p-4">
                <p className="text-xs text-gray-400 mb-1">Skill</p>
                <p className="font-medium text-indigo">{worker.skill}</p>
              </div>
              <div className="bg-cream-alt rounded-xl p-4">
                <p className="text-xs text-gray-400 mb-1">Area</p>
                <p className="font-medium text-indigo">{worker.area}</p>
              </div>
            </div>
          )}
        </div>

        <div className="text-center mt-8 pb-12">
          <a href="/" className="text-sm text-gray-400 hover:text-indigo flex items-center justify-center gap-1">
            <ArrowLeft size={14} />Back to Junction
          </a>
        </div>
      </div>
    </div>
  );
};

export default WorkerDashboard;
